import { expr, stmt, type PineProgram, type PineStmt } from "./ast";
import { buildAlertSpecs } from "./alerts";
import {
  isConditionGraphValid,
  type ConditionGraph,
} from "./conditions";
import { conditionGraphToExpr } from "./conditions-to-ast";
import { renderProgram } from "./render";
import {
  PINE_STRATEGY_TEMPLATES,
  TEMPLATE_IDS,
  templateBody,
  type PineStrategyTemplate,
  type PineStrategyTemplateId,
} from "./templates";
import { validatePineStrategy, type PineValidationReport } from "./validation";

export {
  PINE_STRATEGY_TEMPLATES,
  type PineStrategyTemplate,
  type PineStrategyTemplateId,
};

export type PineStrategyBuilderInput = {
  templateId: PineStrategyTemplateId;
  strategyName: string;
  symbol?: string;
  timeframe?: string;
  fastLength: number;
  slowLength: number;
  riskReward: number;
  stopLossPct: number;
  allowLong: boolean;
  allowShort: boolean;
  includeAlerts: boolean;
  /**
   * Optional visual-composer overrides for the long/short entry conditions.
   * When `root` is non-null, the resulting expression replaces the template's
   * default `longSignal` / `shortSignal` assignment. The template's indicator
   * setup and plot calls are preserved either way.
   */
  conditions?: {
    long?: ConditionGraph;
    short?: ConditionGraph;
  };
  /**
   * Raw-Pine override (B2 - real editor). When set to a non-empty string,
   * `buildPineStrategy` returns this code as-is instead of rendering the
   * template + conditions AST. Validation still runs against it so the
   * static-check verdict reflects the actually-shipped Pine. The visual
   * builder controls remain visible but flagged as "overridden by edits".
   */
  customCode?: string | null;
};

export type { ConditionGraph } from "./conditions";

export type PineStrategyBuildResult = {
  code: string;
  warnings: string[];
  validationNotice: string;
  validation: PineValidationReport;
  program: PineProgram;
  /**
   * True when `code` came from `input.customCode` instead of the AST
   * renderer. The UI uses this to surface "overridden by hand-edits"
   * indicators on the visual controls.
   */
  hasCustomCode: boolean;
};

export const PINE_VALIDATION_NOTICE =
  "Generated Pine is a paste-ready draft, not compiler-verified. Validate it in TradingView before using live.";

const SAFE_TEXT_RE = /[^\w\s:()./-]/g;

function cleanTitle(value: string): string {
  const title = value.replace(SAFE_TEXT_RE, "").replace(/\s+/g, " ").trim();
  return title || "Delta-X Strategy";
}

function cleanNote(value: string | undefined, fallback: string): string {
  const note = value?.replace(SAFE_TEXT_RE, "").replace(/\s+/g, " ").trim();
  return note || fallback;
}

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function clampDecimal(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Number(Math.min(max, Math.max(min, value)).toFixed(2));
}

type Normalized = {
  templateId: PineStrategyTemplateId;
  strategyName: string;
  symbol: string;
  timeframe: string;
  fastLength: number;
  slowLength: number;
  riskReward: number;
  stopLossPct: number;
  takeProfitPct: number;
  allowLong: boolean;
  allowShort: boolean;
  includeAlerts: boolean;
  longConditions: ConditionGraph | null;
  shortConditions: ConditionGraph | null;
};

function normalize(input: PineStrategyBuilderInput): {
  normalized: Normalized;
  warnings: string[];
} {
  const warnings: string[] = [];
  const templateId = TEMPLATE_IDS.has(input.templateId)
    ? input.templateId
    : "ema_trend";
  if (templateId !== input.templateId) {
    warnings.push(
      "Unknown template id; generated code fell back to EMA Trend Follow.",
    );
  }

  const strategyName = cleanTitle(input.strategyName);
  const symbol = cleanNote(input.symbol, "Any symbol");
  const timeframe = cleanNote(input.timeframe, "Chart timeframe");
  const fastLength = clampInteger(input.fastLength, 1, 500);
  const slowLength = clampInteger(input.slowLength, 1, 500);
  const riskReward = clampDecimal(input.riskReward, 0.25, 10);
  const stopLossPct = clampDecimal(input.stopLossPct, 0.05, 20);
  const takeProfitPct = Number((stopLossPct * riskReward).toFixed(2));
  const allowLong = Boolean(input.allowLong);
  const allowShort = Boolean(input.allowShort);

  if (!allowLong && !allowShort) {
    warnings.push(
      "Both long and short entries were disabled; generated code will not place trades.",
    );
  }
  if (fastLength >= slowLength && templateId === "ema_trend") {
    warnings.push(
      "Fast length is not below slow length; crossover behavior may be noisy.",
    );
  }
  if (templateId === "opening_range_breakout") {
    warnings.push(
      "Opening range uses the chart day boundary; verify the exchange session before live use.",
    );
  }

  const longConditions = pickConditionGraph(input.conditions?.long);
  const shortConditions = pickConditionGraph(input.conditions?.short);
  if (input.conditions?.long && !longConditions) {
    warnings.push(
      "Long entry override was malformed; falling back to the template's default long signal.",
    );
  }
  if (input.conditions?.short && !shortConditions) {
    warnings.push(
      "Short entry override was malformed; falling back to the template's default short signal.",
    );
  }

  return {
    warnings,
    normalized: {
      templateId,
      strategyName,
      symbol,
      timeframe,
      fastLength,
      slowLength,
      riskReward,
      stopLossPct,
      takeProfitPct,
      allowLong,
      allowShort,
      includeAlerts: Boolean(input.includeAlerts),
      longConditions,
      shortConditions,
    },
  };
}

function pickConditionGraph(
  graph: ConditionGraph | undefined,
): ConditionGraph | null {
  if (!graph || !graph.root) return null;
  if (!isConditionGraphValid(graph)) return null;
  return graph;
}

function buildProgram(n: Normalized): PineProgram {
  const statements: PineStmt[] = [];

  statements.push(stmt.version());
  statements.push(stmt.comment(PINE_VALIDATION_NOTICE));

  statements.push(
    stmt.strategyDecl(n.strategyName, [
      ["overlay", expr.bool(true)],
      ["initial_capital", expr.num(100000)],
      [
        "commission_type",
        expr.member(expr.member("strategy", "commission"), "percent"),
      ],
      ["commission_value", expr.num(0.01)],
      ["pyramiding", expr.num(0)],
    ]),
  );

  statements.push(stmt.blank());
  statements.push(
    stmt.comment(
      `Built with Delta-X Visual Builder. Target: ${n.symbol} / ${n.timeframe}.`,
    ),
  );

  statements.push(
    stmt.inputInt("fastLength", n.fastLength, "Fast / primary length", {
      minval: 1,
      maxval: 500,
    }),
    stmt.inputInt("slowLength", n.slowLength, "Slow / filter length", {
      minval: 1,
      maxval: 500,
    }),
    stmt.inputFloat("stopLossPct", n.stopLossPct, "Stop loss %", {
      minval: 0.05,
      maxval: 20,
      step: 0.05,
    }),
    stmt.inputFloat("takeProfitPct", n.takeProfitPct, "Take profit %", {
      minval: 0.05,
      maxval: 100,
      step: 0.05,
    }),
    stmt.inputBool("longEnabled", n.allowLong, "Enable long entries"),
    stmt.inputBool("shortEnabled", n.allowShort, "Enable short entries"),
  );

  statements.push(stmt.blank());
  const body = templateBody(n.templateId);
  statements.push(...body.setup);

  const longOverride =
    n.longConditions ? conditionGraphToExpr(n.longConditions) : null;
  const shortOverride =
    n.shortConditions ? conditionGraphToExpr(n.shortConditions) : null;

  statements.push(
    longOverride
      ? stmt.assign("longSignal", longOverride)
      : body.longSignal,
  );
  statements.push(
    shortOverride
      ? stmt.assign("shortSignal", shortOverride)
      : body.shortSignal,
  );

  statements.push(stmt.blank());
  statements.push(
    stmt.assign(
      "activeLongSignal",
      expr.bin("and", expr.id("longEnabled"), expr.id("longSignal")),
    ),
    stmt.assign(
      "activeShortSignal",
      expr.bin("and", expr.id("shortEnabled"), expr.id("shortSignal")),
    ),
  );

  statements.push(stmt.blank());
  // Risk math: close * (1 ± stopLossPct/100) and close * (1 ± takeProfitPct/100)
  const pctOver100 = (id: string) =>
    expr.bin("/", expr.id(id), expr.num(100));
  const onePlusMinus = (op: "+" | "-", id: string) =>
    expr.paren(expr.bin(op, expr.num(1), pctOver100(id)));

  statements.push(
    stmt.assign(
      "longStop",
      expr.bin("*", expr.id("close"), onePlusMinus("-", "stopLossPct")),
    ),
    stmt.assign(
      "longTarget",
      expr.bin("*", expr.id("close"), onePlusMinus("+", "takeProfitPct")),
    ),
    stmt.assign(
      "shortStop",
      expr.bin("*", expr.id("close"), onePlusMinus("+", "stopLossPct")),
    ),
    stmt.assign(
      "shortTarget",
      expr.bin("*", expr.id("close"), onePlusMinus("-", "takeProfitPct")),
    ),
  );

  statements.push(stmt.blank());
  statements.push(
    stmt.ifBlock(expr.id("activeLongSignal"), [
      stmt.callStmt("strategy.entry", [
        expr.str("DZ Long"),
        expr.member("strategy", "long"),
      ]),
      stmt.callStmt(
        "strategy.exit",
        [expr.str("DZ Long Exit"), expr.str("DZ Long")],
        [
          ["stop", expr.id("longStop")],
          ["limit", expr.id("longTarget")],
        ],
      ),
    ]),
  );

  statements.push(stmt.blank());
  statements.push(
    stmt.ifBlock(expr.id("activeShortSignal"), [
      stmt.callStmt("strategy.entry", [
        expr.str("DZ Short"),
        expr.member("strategy", "short"),
      ]),
      stmt.callStmt(
        "strategy.exit",
        [expr.str("DZ Short Exit"), expr.str("DZ Short")],
        [
          ["stop", expr.id("shortStop")],
          ["limit", expr.id("shortTarget")],
        ],
      ),
    ]),
  );

  statements.push(stmt.blank());
  if (n.includeAlerts) {
    // Emitted from buildAlertSpecs so the alertcondition titles, the UI
    // webhook helper, and the JSON payload schema all agree on the same
    // deterministic names. See src/lib/pine/alerts.ts.
    const specs = buildAlertSpecs({
      strategyName: n.strategyName,
      includeAlerts: n.includeAlerts,
    });
    for (const spec of specs) {
      statements.push(
        stmt.callStmt("alertcondition", [
          expr.id(
            spec.side === "long" ? "activeLongSignal" : "activeShortSignal",
          ),
          expr.str(spec.name),
          expr.str(spec.message),
        ]),
      );
    }
  } else {
    statements.push(stmt.comment("Alerts disabled in builder."));
  }

  statements.push(stmt.blank());
  statements.push(
    stmt.callStmt(
      "plotshape",
      [expr.id("activeLongSignal")],
      [
        ["title", expr.str("Long Signal")],
        ["style", expr.member("shape", "triangleup")],
        ["location", expr.member("location", "belowbar")],
        ["color", expr.member("color", "lime")],
        ["size", expr.member("size", "tiny")],
      ],
    ),
    stmt.callStmt(
      "plotshape",
      [expr.id("activeShortSignal")],
      [
        ["title", expr.str("Short Signal")],
        ["style", expr.member("shape", "triangledown")],
        ["location", expr.member("location", "abovebar")],
        ["color", expr.member("color", "red")],
        ["size", expr.member("size", "tiny")],
      ],
    ),
  );

  return { statements };
}

export function buildPineStrategy(
  input: PineStrategyBuilderInput,
): PineStrategyBuildResult {
  const { normalized, warnings } = normalize(input);
  const program = buildProgram(normalized);
  const generated = renderProgram(program);

  // Hand-edited override: ship the user's literal Pine instead of the
  // freshly-rendered code. Validation still runs against the actual code
  // that will hit TradingView so the verdict cannot be stale.
  const customCode =
    typeof input.customCode === "string" && input.customCode.trim().length > 0
      ? input.customCode
      : null;
  const code = customCode ?? generated;
  const hasCustomCode = customCode !== null;
  if (hasCustomCode) {
    warnings.push(
      "Custom Pine edits are active. Visual builder controls are still saved but the generated code below comes from your hand-edits.",
    );
  }
  return {
    code,
    warnings,
    validationNotice: PINE_VALIDATION_NOTICE,
    validation: validatePineStrategy(code),
    program,
    hasCustomCode,
  };
}
